
import { useState, useRef, useEffect } from 'react';

const AudioPlayer = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const slideIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const slides = [
    {
      image: 'https://public.readdy.ai/ai/img_res/a40be7d16fdb11cb8a61efc3f1839de5.jpg',
      alt: 'Incoming call interface',
      caption: 'AI answers instantly'
    },
    {
      image: 'https://readdy.ai/api/search-image?query=Professional%20AI%20assistant%20interface%20on%20computer%20screen%20showing%20customer%20service%20dashboard%2C%20modern%20clean%20UI%20with%20chat%20bubbles%20and%20response%20suggestions%2C%20soft%20blue%20and%20white%20color%20scheme%2C%20minimalist%20design%2C%20office%20desk%20setting%2C%20natural%20lighting%2C%20premium%20business%20technology%20photography&width=1200&height=800&seq=demoslide2&orientation=landscape',
      alt: 'AI answering interface',
      caption: 'FAQs handled professionally'
    },
    {
      image: 'https://readdy.ai/api/search-image?query=Clean%20FAQ%20interface%20display%20with%20organized%20question%20icons%20and%20answer%20cards%2C%20modern%20minimalist%20design%2C%20soft%20pastel%20colors%2C%20professional%20business%20setting%2C%20tablet%20or%20computer%20screen%20showing%20knowledge%20base%20system%2C%20bright%20office%20environment%2C%20premium%20corporate%20photography%20style&width=1200&height=800&seq=demoslide3&orientation=landscape',
      alt: 'FAQ handling system',
      caption: 'Customer questions answered confidently'
    },
    {
      image: 'https://readdy.ai/api/search-image?query=Modern%20CRM%20software%20interface%20showing%20new%20contact%20saved%20confirmation%2C%20clean%20dashboard%20with%20customer%20details%20form%20filled%20out%2C%20professional%20business%20application%20design%2C%20soft%20green%20success%20indicators%2C%20minimalist%20UI%2C%20office%20desk%20with%20laptop%2C%20natural%20lighting%2C%20premium%20technology%20photography&width=1200&height=800&seq=demoslide4&orientation=landscape',
      alt: 'Lead captured in CRM',
      caption: 'Lead captured into CRM'
    },
    {
      image: 'https://readdy.ai/api/search-image?query=Digital%20calendar%20application%20showing%20confirmed%20appointment%20booking%2C%20clean%20modern%20interface%20with%20time%20slots%20and%20green%20confirmation%20checkmark%2C%20professional%20scheduling%20software%20design%2C%20soft%20colors%2C%20minimalist%20style%2C%20computer%20screen%20in%20bright%20office%2C%20premium%20business%20photography&width=1200&height=800&seq=demoslide5&orientation=landscape',
      alt: 'Appointment booked',
      caption: 'Appointment booked'
    },
    {
      image: 'https://readdy.ai/api/search-image?query=Minimalist%20business%20dashboard%20showing%20upward%20trending%20revenue%20graph%20and%20positive%20growth%20metrics%2C%20clean%20modern%20analytics%20interface%2C%20soft%20green%20and%20blue%20colors%2C%20professional%20data%20visualization%2C%20simple%20elegant%20design%2C%20laptop%20screen%20in%20contemporary%20office%2C%20natural%20lighting%2C%20premium%20corporate%20photography&width=1200&height=800&seq=demoslide6&orientation=landscape',
      alt: 'Revenue protected',
      caption: 'Call outcome tracked'
    }
  ];

  const posterImage = 'https://readdy.ai/api/search-image?query=Professional%20business%20receptionist%20wearing%20headset%20smiling%20while%20answering%20phone%20call%20in%20modern%20bright%20office%2C%20clean%20minimalist%20workspace%2C%20soft%20natural%20lighting%2C%20corporate%20professional%20atmosphere%2C%20premium%20business%20photography%2C%20welcoming%20customer%20service%20aesthetic%2C%20high%20quality%20commercial%20style&width=1200&height=800&seq=demoposter&orientation=landscape';
  const posterCaption = 'Incoming call from a prospective customer…';

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const startSlideshow = () => {
    if (slideIntervalRef.current) {
      clearInterval(slideIntervalRef.current);
    }
    slideIntervalRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
  };

  const stopSlideshow = () => {
    if (slideIntervalRef.current) {
      clearInterval(slideIntervalRef.current);
      slideIntervalRef.current = null;
    }
  };

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        stopSlideshow();
      } else {
        audioRef.current.play();
        startSlideshow();
      }
      setIsPlaying(!isPlaying);
    }
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleEnded = () => {
      setIsPlaying(false);
      stopSlideshow();
      setCurrentSlide(0);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);

    return () => {
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      stopSlideshow();
    };
  }, []);

  return (
    <div className="relative w-full max-w-5xl mx-auto">
      <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden" style={{ aspectRatio: '16/9' }}>
        {/* 24/7 Badge */}
        <div className="absolute top-6 right-6 z-30 bg-pink-500 text-white px-4 py-2 rounded-full font-bold text-sm shadow-lg">
          24/7
        </div>

        {/* Background Images */}
        <div className="absolute inset-0">
          {!isPlaying ? (
            <img
              src={posterImage}
              alt="ReceptionGenie Demo"
              className="w-full h-full object-cover"
            />
          ) : (
            <>
              {slides.map((slide, index) => (
                <img
                  key={index}
                  src={slide.image}
                  alt={slide.alt}
                  className="absolute inset-0 w-full h-full object-cover transition-opacity duration-600"
                  style={{
                    opacity: currentSlide === index ? 1 : 0,
                    zIndex: currentSlide === index ? 1 : 0
                  }}
                />
              ))}
            </>
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/5 to-black/10"></div>
        </div>

        {/* Waveform Overlay */}
        {isPlaying && (
          <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
            <div className="flex items-center gap-1">
              {[...Array(40)].map((_, i) => (
                <div
                  key={i}
                  className="bg-white rounded-full animate-pulse"
                  style={{
                    width: '5px',
                    height: `${Math.random() * 60 + 20}px`,
                    animationDelay: `${i * 0.05}s`,
                    animationDuration: '0.8s',
                    filter: 'drop-shadow(0 0 35px rgba(236, 72, 153, 0.8))'
                  }}
                ></div>
              ))}
            </div>
          </div>
        )}

        {/* Play Button */}
        <div className="absolute inset-0 flex items-center justify-center z-20">
          <button
            onClick={handlePlayPause}
            className="group bg-white/95 hover:bg-white rounded-full px-8 py-4 shadow-2xl transition-all duration-300 hover:scale-105 cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 flex items-center justify-center bg-pink-500 rounded-full group-hover:bg-pink-600 transition-colors">
                <i className={`${isPlaying ? 'ri-pause-fill' : 'ri-play-fill'} text-white text-2xl`}></i>
              </div>
              <span className="text-gray-900 font-bold text-lg whitespace-nowrap">
                {isPlaying ? 'Playing a Real Customer Call' : '▶ Hear a Real Call'}
              </span>
            </div>
          </button>
        </div>

        {/* Slide Caption - Bottom Left */}
        <div className="absolute bottom-6 left-6 z-30">
          <div className="bg-black/70 backdrop-blur-sm text-white px-4 py-2 rounded-lg text-sm font-medium transition-opacity duration-500">
            {!isPlaying ? posterCaption : slides[currentSlide].caption}
          </div>
        </div>

        {/* Timestamp - Bottom Right */}
        <div className="absolute bottom-6 right-6 z-30">
          <div className="bg-black/70 backdrop-blur-sm text-white px-4 py-2 rounded-lg text-sm font-mono">
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>
        </div>

        {/* Hidden Audio Element */}
        <audio
          ref={audioRef}
          src="https://storage.googleapis.com/msgsndr/ufGgg1PmwgWzIDRrG0qr/media/695dd0b90597df70a36eb8ee.wav"
          preload="auto"
        />
      </div>

      {/* Caption */}
      <p className="text-center text-gray-600 text-sm mt-4">
        This is an actual call answered by ReceptionGenie. No scripting.
      </p>
    </div>
  );
};

export default AudioPlayer;
